// apps/client/src/components/common/DateRangePicker.tsx
// Date range picker component using react-day-picker with dark theme styling

import React, { useState, useRef, useEffect } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import { format, parse, isValid } from 'date-fns';
import { CalendarIcon } from '@heroicons/react/24/outline';
import 'react-day-picker/dist/style.css';

interface DateRangePickerProps {
  startDate: string | undefined; // YYYY-MM-DD format
  endDate: string | undefined; // YYYY-MM-DD format
  onRangeChange: (start: string | undefined, end: string | undefined) => void;
  label?: string;
  id?: string;
}

export function DateRangePicker({
  startDate,
  endDate,
  onRangeChange,
  label = 'Settlement Date',
  id = 'date-range',
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse string dates to Date objects
  const parseDate = (dateStr: string | undefined): Date | undefined => {
    if (!dateStr) return undefined;
    const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? parsed : undefined;
  };

  const range: DateRange = {
    from: parseDate(startDate),
    to: parseDate(endDate),
  };

  // Format for display (Australian format: dd/MM/yyyy)
  const formatDisplay = (date: Date | undefined): string => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };

  const displayValue = (): string => {
    const fromStr = formatDisplay(range.from);
    const toStr = formatDisplay(range.to);

    if (fromStr && toStr) return `${fromStr} - ${toStr}`;
    if (fromStr) return `${fromStr} - ...`;
    if (toStr) return `... - ${toStr}`;
    return 'Select date range';
  };

  // Handle date selection
  const handleSelect = (newRange: DateRange | undefined) => {
    const formatToYMD = (date: Date | undefined): string | undefined => {
      if (!date) return undefined;
      return format(date, 'yyyy-MM-dd');
    };

    onRangeChange(formatToYMD(newRange?.from), formatToYMD(newRange?.to));
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-2">
        {label}
      </label>
      <button
        id={id}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-gray-650 transition-colors"
      >
        <span className={range.from || range.to ? 'text-white' : 'text-gray-400'}>
          {displayValue()}
        </span>
        <CalendarIcon className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-xl p-4 right-0">
          <style>
            {`
              .rdp {
                --rdp-cell-size: 36px;
                --rdp-accent-color: #2563eb;
                --rdp-background-color: #1f2937;
                margin: 0;
              }
              .rdp-months {
                display: flex;
                gap: 1rem;
              }
              .rdp-month {
                margin: 0;
              }
              .rdp-caption {
                display: flex;
                justify-content: center;
                padding: 0 0.5rem;
                margin-bottom: 0.5rem;
              }
              .rdp-caption_label {
                font-size: 0.875rem;
                font-weight: 500;
                color: white;
              }
              .rdp-nav {
                display: flex;
                gap: 0.25rem;
              }
              .rdp-nav_button {
                width: 28px;
                height: 28px;
                background: transparent;
                border: none;
                border-radius: 4px;
                color: #9ca3af;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .rdp-nav_button:hover {
                background: #374151;
                color: white;
              }
              .rdp-table {
                border-collapse: collapse;
              }
              .rdp-head_cell {
                font-size: 0.75rem;
                font-weight: normal;
                color: #9ca3af;
                padding: 0.25rem 0;
                text-align: center;
                width: var(--rdp-cell-size);
              }
              .rdp-cell {
                padding: 0;
              }
              .rdp-day {
                width: var(--rdp-cell-size);
                height: var(--rdp-cell-size);
                font-size: 0.875rem;
                color: #d1d5db;
                border-radius: 6px;
                cursor: pointer;
                background: transparent;
                border: none;
              }
              .rdp-day:hover:not(.rdp-day_selected):not(.rdp-day_disabled) {
                background: #374151;
              }
              .rdp-day_today {
                border: 1px solid #3b82f6;
                color: #60a5fa;
              }
              .rdp-day_selected {
                background: #2563eb !important;
                color: white !important;
              }
              .rdp-day_range_start,
              .rdp-day_range_end {
                background: #2563eb !important;
                color: white !important;
              }
              .rdp-day_range_middle {
                background: rgba(37, 99, 235, 0.3) !important;
                color: #93c5fd !important;
                border-radius: 0;
              }
              .rdp-day_outside {
                color: #4b5563;
              }
              .rdp-day_disabled {
                color: #4b5563;
                cursor: not-allowed;
              }
            `}
          </style>
          <DayPicker
            mode="range"
            selected={range}
            onSelect={handleSelect}
            numberOfMonths={1}
            showOutsideDays
          />
          <div className="mt-4 flex justify-between border-t border-gray-700 pt-4">
            <button
              type="button"
              onClick={() => {
                onRangeChange(undefined, undefined);
              }}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
